<!-- handles both adding new and updating existing connection -->
<template>
    <Head title="Edit Connection" />
    <BaseLayout gap="sm" fill-height>
        <template #header>
            <BaseLink type="button" class="flex gap-2 items-center text-valence-grey-750 ml-6 md:ml-0 mt-4 w-fit" :href="connection_detail_url ?? connections_page_url">
                <i class="bi bi-chevron-left icon-bold" aria-hidden="true"></i> <span class="body-small">Back</span>
            </BaseLink>
        </template>
        <BasePageHeader :title="isNew ? 'Add new person' : 'Edit person'" class="text-left" />
        <!-- Calendar suggestions loading state -->
        <div v-if="shouldShowCalendarSuggestions && isFetchingSuggestions" class="w-full md:px-8 md:pt-10 md:pb-6 lg:px-14 md:border border-border-strong rounded-xl md:bg-white">
            <div class="mb-4">
                <h2 class="body-small-bold mb-1">Suggestions from your calendar</h2>
                <p class="details text-gray-600">Loading suggestions...</p>
            </div>
            <div class="flex items-center justify-center py-12">
                <LoadingSpinner class="w-6 h-6" />
            </div>
        </div>

        <!-- Calendar suggestions - single row at top -->
        <div
            v-else-if="shouldShowCalendarSuggestions && suggestions.length > 0"
            class="w-full md:px-8 md:pt-10 md:pb-6 lg:px-14 md:border border-border-strong rounded-xl md:bg-white">
            <div class="mb-4">
                <h2 class="body-small-bold mb-1">Suggestions from your calendar</h2>
                <p class="details text-gray-600">Click a person to fill the form</p>
            </div>
            <div class="relative">
                <div
                    ref="suggestionsScrollContainer"
                    role="region"
                    aria-label="Calendar suggestions"
                    class="flex gap-3 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 scroll-snap-x scroll-mandatory">
                    <button
                        v-for="person in suggestions"
                        :key="person.email"
                        type="button"
                        :aria-label="`Select ${person.name}, ${person.meeting_count} ${person.meeting_count === 1 ? 'meeting' : 'meetings'}`"
                        :aria-pressed="selectedSuggestionEmail === person.email"
                        class="shrink-0 w-48 sm:w-56 md:w-52 lg:w-56 p-3 rounded-lg border bg-white cursor-pointer transition-colors hover:border-valence-primary-300 hover:bg-valence-primary-50 text-left scroll-snap-start"
                        :class="[selectedSuggestionEmail === person.email ? 'border-valence-primary-500 bg-valence-primary-50' : 'border-border-strong']"
                        @click="handleSuggestionClick(person)">
                        <div class="flex items-center gap-2.5 mb-2">
                            <div
                                class="w-10 h-10 rounded-full flex items-center justify-center body-small-semibold flex-shrink-0"
                                :class="[person.has_public_profile ? 'bg-[#D4F4E4] text-[#0A6B3C]' : 'bg-[#FFE4D6] text-[#B54708]']"
                                translate="no">
                                {{ getInitials(person.name) }}
                            </div>
                            <div class="flex-1 min-w-0">
                                <div class="flex items-center gap-1.5 mb-0.5">
                                    <span class="body-small-semibold truncate">{{ person.name }}</span>
                                    <span
                                        v-if="person.has_public_profile"
                                        class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[#D4F4E4] text-[#0A6B3C] flex-shrink-0">
                                        <i class="bi text-[8px]" :class="COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS" aria-hidden="true"></i>
                                        Collab profile
                                    </span>
                                </div>
                                <div class="text-gray-500">
                                    <span v-if="person.title" class="body-small truncate block">{{ person.title }}</span>
                                    <span v-else class="body-small truncate text-gray-400 block">{{ person.email }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="details text-gray-600">{{ person.meeting_count }} {{ person.meeting_count === 1 ? "meeting" : "meetings" }}</div>
                    </button>
                </div>
                <!-- Scroll arrow buttons -->
                <button
                    v-if="showLeftFade"
                    type="button"
                    class="absolute left-0 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white border border-border-strong shadow-md flex items-center justify-center text-valence-primary-500 hover:bg-valence-primary-50 hover:border-valence-primary-300 transition-colors"
                    aria-label="Scroll left through suggestions"
                    @click="scroll('left')">
                    <i class="bi bi-chevron-left text-sm" aria-hidden="true"></i>
                </button>
                <button
                    v-if="showRightFade"
                    type="button"
                    class="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white border border-border-strong shadow-md flex items-center justify-center text-valence-primary-500 hover:bg-valence-primary-50 hover:border-valence-primary-300 transition-colors"
                    aria-label="Scroll right through suggestions"
                    @click="scroll('right')">
                    <i class="bi bi-chevron-right text-sm" aria-hidden="true"></i>
                </button>
            </div>
            <div v-if="from_calendar_url" class="mt-4 pt-4 border-t border-border-strong">
                <BaseLink :href="from_calendar_url" class="body-small text-valence-primary-500 hover:underline flex items-center gap-1">
                    <span>See more</span>
                    <i class="bi bi-arrow-right" aria-hidden="true"></i>
                </BaseLink>
            </div>
        </div>
        <form class="w-full mt-4 md:mt-0" @submit.prevent="handleSave">
            <div class="flex flex-col gap-8 grid-container">
                <div class="md:px-8 md:py-10 lg:px-14 md:border border-border-strong rounded-xl grid gap-8 md:bg-white form-grid">
                    <FormElement title="Name *" for="name">
                        <input
                            id="name"
                            ref="nameInput"
                            v-model="form.name"
                            type="text"
                            placeholder="Required"
                            required
                            class="body-small rounded-lgr p-2 w-full focus-visible:outline-none focus-visible:border-control-border-focus border border-border-strong fs-mask invalid:border-red-600" />
                    </FormElement>
                    <FormElement title="Job Title *" for="jobTitle">
                        <input
                            id="jobTitle"
                            v-model="form.jobTitle"
                            type="text"
                            placeholder="Required"
                            required
                            class="body-small rounded-lgr p-2 w-full focus-visible:outline-none focus-visible:border-control-border-focus border border-border-strong fs-mask invalid:border-red-600" />
                    </FormElement>
                    <FormElement v-if="public_profile_enabled" title="Email" for="email">
                        <input
                            id="email"
                            v-model="form.email"
                            type="email"
                            placeholder="Optional"
                            class="body-small focus-visible:outline-none focus-visible:border-control-border-focus rounded-lgr p-2 w-full border border-border-strong fs-mask" />
                    </FormElement>
                    <fieldset>
                        <legend class="body-small mb-3">Network</legend>
                        <div class="flex gap-x-10 gap-y-2 flex-wrap checkbox-container">
                            <BaseCheckbox v-model="form.inTeam"> In My Team </BaseCheckbox>
                            <BaseCheckbox v-model="form.inOrg"> In My Organization </BaseCheckbox>
                        </div>
                    </fieldset>
                    <template v-if="facets.length > 0">
                        <FormElement v-for="facet in facets" :key="facet.id" :title="facet.name" :for="`facet-${facet.id}`">
                            <input
                                :id="`facet-${facet.id}`"
                                v-model="form.facetValues[facet.id]"
                                type="text"
                                placeholder="Optional"
                                class="body-small rounded-lg p-2 w-full border border-border-strong fs-mask" />
                        </FormElement>
                    </template>
                    <FormElement title="How comfortable are you collaborating with this person?">
                        <BaseSlider
                            v-model="form.influence"
                            :max="4"
                            :step="1"
                            slider-label="How comfortable are you collaborating with this person?"
                            :thumb-value-label="['Not very', 'Slightly', 'Somewhat', 'Fairly', 'Very']"
                            label-start="Not very"
                            label-end="Very" />
                    </FormElement>
                </div>
                <div v-if="isNew" class="md:px-8 md:py-10 lg:px-14 md:border border-border-strong rounded-xl flex flex-col gap-3 md:bg-white">
                    <p class="body-small">Notes</p>
                    <p class="body-small font-normal">The more I know about a person, the more helpful and personalized my insights will be.</p>
                    <div class="border border-border-strong rounded-lg p-2 flex flex-col">
                        <BaseTextarea v-model:input="input" rows="6" class="font-normal mt-2" aria-label="Notes" placeholder="Type or speak here" />
                        <button
                            v-if="!isTranscribing"
                            class="bi text-2xl/none self-end"
                            :class="[isRecording ? 'bi-stop-circle text-red-500' : 'bi-mic']"
                            :aria-label="isRecording ? 'Stop recording note' : 'Start recording note'"
                            :aria-pressed="isRecording"
                            type="button"
                            @click.prevent.stop="handleRecord" />
                        <LoadingSpinner v-else class="w-6 h-6 self-end" />
                    </div>
                </div>
            </div>
            <BaseButton type="submit" theme="primary" class="body-small float-right my-10" :disabled="!isFormValid || isSaving">
                <span v-if="!isSaving">{{ isNew ? "Add Team Member" : "Update Team Member" }}</span>
                <LoadingSpinner v-else class="w-6 h-6" />
            </BaseButton>
        </form>
        <p v-if="errorMessage" class="text-error font-medium">{{ errorMessage }}</p>
    </BaseLayout>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
import type { CalendarPeopleResponse, CalendarPerson, ConnectionDict, FacetDefinition } from "~vue/types/connection";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useElementSize, useScroll } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { GlobalProperties } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import BaseCheckbox from "~vue/components/form/BaseCheckbox.vue";
import BaseSlider from "~vue/components/form/BaseSlider.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import FormElement from "~vue/components/form/FormElement.vue";
import { COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS } from "~vue/components/navigation/menuPanes";
import { useRecorder } from "~vue/composables/useRecorder";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { getInitials } from "~vue/utils/stringUtils";
import { computed, inject, nextTick, onMounted, onUnmounted, reactive, ref, useTemplateRef } from "vue";

import { AudioRecorder } from "../../AudioRecorder";

// globalProperties is injected at the app.js level, which is available in all components, safe type cast on the default value here
const { $setEventContext } = inject<GlobalProperties>("globalProperties", {} as GlobalProperties);

const {
    connection,
    event_context,
    connection_detail_url,
    connections_page_url,
    calendar_people_api_url,
    from_calendar_url,
    calendar_import_enabled,
    has_calendar_integration,
    public_profile_enabled,
    facets = [],
} = defineProps<{
    connection?: ConnectionDict;
    connections_page_url: string;
    connection_detail_url?: string;
    event_context: Record<string, string>;
    calendar_people_api_url?: string;
    from_calendar_url?: string;
    calendar_import_enabled?: boolean;
    has_calendar_integration?: boolean;
    public_profile_enabled: boolean;
    facets?: FacetDefinition[];
}>();

const recorder = ref(new AudioRecorder());
const { isTranscribing, isRecording, handleRecord, input } = useRecorder({ recorder: recorder.value });
const nameInput = useTemplateRef<HTMLInputElement>("nameInput");

const isNew = computed(() => !connection || !connection.id);
const shouldShowCalendarSuggestions = computed(() => isNew.value && calendar_import_enabled && has_calendar_integration);

// Build initial facet values map from connection's existing facet_values
const initialFacetValues: Record<string, string> = {};
if (connection?.facet_values) {
    for (const fv of connection.facet_values) {
        initialFacetValues[fv.facet_id] = fv.value;
    }
}

const form = reactive({
    name: connection?.persona?.name ?? "",
    jobTitle: connection?.connection?.job_title ?? "",
    email: public_profile_enabled ? (connection?.alias?.value ?? "") : "",
    aliasId: connection?.alias?.id,
    inTeam: connection?.connection?.in_team ?? false,
    inOrg: connection?.connection?.in_org ?? false,
    influence: [connection?.connection?.influence ?? 2],
    facetValues: { ...initialFacetValues } as Record<string, string>,
});
const selectedSuggestionEmail = ref<string | null>(null);

const isFormValid = computed(() => {
    const influence = form.influence[0] ?? 0;
    const isformElementsValid = form.name && form.jobTitle && influence >= 0;
    return isformElementsValid;
});

const facetValuesPayload = computed(() =>
    facets.length > 0
        ? facets.map((f) => ({
              facet_id: f.id,
              value: form.facetValues[f.id] ?? "",
          }))
        : [],
);

const payload = computed(() => ({
    job_title: form.jobTitle,
    in_team: form.inTeam,
    in_org: form.inOrg,
    persona: {
        name: form.name,
        relationship: form.jobTitle,
    },
    influence: form.influence[0],
    note: { raw_note: input.value },
    ...(public_profile_enabled && {
        alias: {
            ...(form.aliasId && { id: form.aliasId }),
            value: form.email,
        },
    }),
    ...(facets.length > 0 && { facet_values: facetValuesPayload.value }),
}));

const {
    error: createError,
    execute: executeCreate,
    onFetchResponse: onCreateResponse,
    isFetching: isCreating,
} = useFetch("/api/connections/", { immediate: false }).post(payload).json();
const {
    error: updateError,
    execute: executeUpdate,
    onFetchResponse: onUpdateResponse,
    isFetching: isUpdating,
} = useFetch(`/api/connections/${connection?.id}/`, { immediate: false })
    .put(computed(() => ({ ...payload.value, declined_at: null })))
    .json();

onCreateResponse(() => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_ADD, { source: "connection_edit", added_context: input.value?.length > 0 });
    router.visit(connections_page_url);
});
onUpdateResponse(() => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_UPDATE, { source: "connection_edit" });
    router.visit(connection_detail_url ?? connections_page_url);
});

const errorMessage = computed(() => {
    const error = createError.value || updateError.value;
    return isSessionEndedError(error) ? null : error?.message;
});

const handleSave = () => {
    createError.value = "";
    updateError.value = "";
    if (isNew.value) {
        executeCreate();
    } else {
        executeUpdate();
    }
};

const isSaving = computed(() => isCreating.value || isUpdating.value);

// Calendar suggestions
const suggestions = ref<CalendarPerson[]>([]);
const suggestionsScrollContainer = ref<HTMLElement>();

const { arrivedState } = useScroll(suggestionsScrollContainer, {
    behavior: "smooth",
});

const { width } = useElementSize(suggestionsScrollContainer);

// Check if content overflows container
const hasOverflow = computed(() => {
    const container = suggestionsScrollContainer.value;
    if (!container) {
        return false;
    }
    return width.value > 0 && container.scrollWidth > container.clientWidth;
});

// Arrow visibility based on scroll position and overflow
const showLeftFade = computed(() => hasOverflow.value && !arrivedState.left);
const showRightFade = computed(() => hasOverflow.value && !arrivedState.right);

const scroll = (direction: "left" | "right") => {
    const container = suggestionsScrollContainer.value;
    if (!container) {
        return;
    }

    // Scroll by container width to show next set of cards
    const scrollAmount = direction === "left" ? -container.clientWidth : container.clientWidth;
    container.scrollBy({ left: scrollAmount, behavior: "smooth" });
};

// Fetch calendar suggestions - useFetch must be called at top level per Vue style guide
const {
    data: calendarData,
    execute: fetchSuggestions,
    onFetchResponse,
    isFetching: isFetchingSuggestions,
} = useFetch(calendar_people_api_url || "", { immediate: false })
    .get()
    .json<CalendarPeopleResponse>();

onFetchResponse(() => {
    if (calendarData.value?.people) {
        suggestions.value = calendarData.value.people.slice(0, 5);
    }
});

// Conditionally fetch suggestions when calendar import is enabled and it's a new connection
if (shouldShowCalendarSuggestions.value && calendar_people_api_url) {
    fetchSuggestions();
}

const handleSuggestionClick = (person: CalendarPerson) => {
    form.name = person.name;
    form.jobTitle = person.title || "";

    if (public_profile_enabled) {
        form.email = person.email;
    }

    selectedSuggestionEmail.value = person.email;

    logUserInteraction(AmplitudeEventNames.CONNECTION_SUGGESTION_CLICKED, {
        email: person.email,
        has_public_profile: person.has_public_profile,
        meeting_count: person.meeting_count,
    });

    // Focus name input to confirm selection
    nextTick(() => {
        nameInput.value?.focus();
    });
};

onMounted(() => {
    $setEventContext(event_context);
});

onUnmounted(() => {
    $setEventContext({});
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";
.grid-container {
    container-type: inline-size;
}

.form-grid {
    grid-template-columns: repeat(1, 1fr);
}

@container (min-width: 500px) {
    .form-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

.checkbox-container :deep(.check) {
    @apply text-valence-primary-500;
}
</style>
